
import { useState, useEffect, useCallback } from 'react';

const FAVORITES_KEY = 'vacation_favorites';

// Global state to ensure all hook instances share the same data
let globalFavorites: number[] = [];
let globalListeners: Set<(favorites: number[]) => void> = new Set();

// Load initial favorites from localStorage
try {
  const stored = localStorage.getItem(FAVORITES_KEY);
  if (stored) {
    const parsed = JSON.parse(stored);
    globalFavorites = Array.isArray(parsed) ? parsed : [];
  }
} catch (error) {
  console.error('Error loading favorites from localStorage:', error);
  globalFavorites = [];
}

const notifyListeners = (favorites: number[]) => {
  globalListeners.forEach(listener => listener(favorites));
};

const saveFavorites = (favorites: number[]) => {
  globalFavorites = favorites;
  try {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('favoritesChanged', { detail: favorites }));
    notifyListeners(favorites);
  } catch (error) {
    console.error('Error saving favorites to localStorage:', error);
  }
};

export const useLocalFavorites = () => {
  const [favorites, setFavorites] = useState<number[]>(globalFavorites);

  // Subscribe to global state changes
  useEffect(() => {
    const listener = (newFavorites: number[]) => {
      setFavorites(newFavorites);
    };
    globalListeners.add(listener);
    
    return () => {
      globalListeners.delete(listener);
    };
  }, []);

  // Listen for storage changes from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === FAVORITES_KEY && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          const newFavorites = Array.isArray(parsed) ? parsed : [];
          globalFavorites = newFavorites;
          notifyListeners(newFavorites);
        } catch (error) {
          console.error('Error parsing favorites from storage event:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const addFavorite = useCallback((vacationId: number) => {
    if (globalFavorites.includes(vacationId)) return;
    const newFavorites = [...globalFavorites, vacationId];
    saveFavorites(newFavorites);
  }, []);

  const removeFavorite = useCallback((vacationId: number) => {
    const newFavorites = globalFavorites.filter(id => id !== vacationId);
    saveFavorites(newFavorites);
  }, []);

  const toggleFavorite = useCallback((vacationId: number) => {
    const newFavorites = globalFavorites.includes(vacationId)
      ? globalFavorites.filter(id => id !== vacationId)
      : [...globalFavorites, vacationId];
    saveFavorites(newFavorites);
  }, []);

  const isFavorite = useCallback((vacationId: number) => {
    return globalFavorites.includes(vacationId);
  }, []);

  const clearFavorites = useCallback(() => {
    saveFavorites([]);
  }, []);

  return {
    favorites,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    clearFavorites,
    favoritesCount: favorites.length
  };
};
